import React, { useEffect, useState } from 'react';
import api from '../api';
import DietPlanSystem from './DietPlanSystem';

function DietPlansList({ onBack }) {
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Fetch all diet plans on mount
  useEffect(() => {
    const fetchDietPlans = async () => {
      setLoading(true);
      try {
        const response = await api.get('/dietplan/user-dietplans');
        console.log('Diet plans response:', response.data); // Debug log
        setDietPlans(Array.isArray(response.data) ? response.data : response.data.dietPlans || []);
      } catch (err) {
        console.error('Error fetching diet plans:', err.response?.data || err.message);
        alert('Failed to load diet plans. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchDietPlans();
  }, []);

  const handleSelectPlan = async (plan, index) => {
    // Handle case where plan might be a string (ID) or an object
    let planId;
    if (typeof plan === 'string') {
      planId = plan;
    } else {
      // Extract ID from various possible fields
      planId = plan.id || plan._id || plan.planId || plan.dietPlanId;
    }

    console.log('Selected plan ID:', planId); // Debug log

    if (!planId) {
      alert('Unable to find plan ID. Please check the data structure.');
      return;
    }

    setSelectedPlanId(planId);
    setLoadingPlan(true);
    try {
      const response = await api.get(`/dietplan/${planId}`);
      setSelectedPlanData(response.data);
    } catch (err) {
      console.error('Error fetching diet plan details:', err.response?.data || err.message);
      alert('Failed to load diet plan details. Please try again.');
      setSelectedPlanId(null);
    } finally {
      setLoadingPlan(false);
    }
  };

  // If a plan is selected, show the diet plan display
  if (selectedPlanData) {
    return (
      <div className="content-panel-wide diet-plan-list-page">
        <button className="back-btn" onClick={() => { setSelectedPlanData(null); setSelectedPlanId(null); }}>
          &#8592; Back to All Plans
        </button>
        <DietPlanSystem data={selectedPlanData} />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="content-panel-wide diet-plan-list-page" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--color-border)',
          borderTopColor: 'var(--color-secondary)',
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
          margin: '0 auto',
        }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.95rem', marginTop: '16px' }}>
          Loading your diet plans…
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Empty state
  if (dietPlans.length === 0) {
    return (
      <div className="content-panel-wide diet-plan-list-page">
        <button className="back-btn" onClick={onBack}>
          &#8592; Back
        </button>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--color-text)', marginBottom: '12px' }}>
            No Diet Plans Yet 🥗
          </h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.95rem', marginBottom: '24px' }}>
            Create your first personalized diet plan to get started.
          </p>
          <button
            onClick={onBack}
            style={{
              padding: '12px 24px',
              background: 'var(--color-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Create Diet Plan
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="content-panel-wide diet-plan-list-page">
      <button className="back-btn" onClick={onBack}>
        &#8592; Back
      </button>

      <div className="diet-plan-list-heading">
        <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--color-text)', marginBottom: '6px' }}>
          Your Diet Plans 📋
        </h2>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
          Total plans: <strong>{dietPlans.length}</strong>
        </p>
      </div>

      <div className="diet-plan-list-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '16px',
      }}>
        {dietPlans.map((plan, index) => {
          // Extract ID from the plan object
          const planId = plan.id;
          const createdDate = formatDate(plan.date);
          const weight = plan.weight;

          return (
            <div
              className="diet-plan-list-card"
              key={planId || index}
              onClick={() => handleSelectPlan(planId, index)}
              style={{
                padding: '20px',
                background: 'var(--color-card)',
                border: selectedPlanId === planId ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: loadingPlan ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: selectedPlanId === planId && loadingPlan ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loadingPlan) {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 82, 30, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedPlanId !== planId) {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                margin: '0 0 12px 0',
              }}>
                Diet Plan {index + 1}
              </h3>

              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                <p style={{ margin: '6px 0' }}>📅 Created on {createdDate}</p>
                <p style={{ margin: '6px 0' }}>⚖️ with weight {weight} kg</p>
              </div>

              {selectedPlanId === planId && loadingPlan && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid var(--color-border)',
                    borderTopColor: 'var(--color-secondary)',
                    borderRadius: '50%',
                    animation: 'spin 0.9s linear infinite',
                    display: 'inline-block',
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default DietPlansList;
